import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  confirmImagePreview as confirmImagePreviewClient,
  requestImagePreviewForDraft,
} from "@/features/image-generation";
import type {
  ConfirmImagePreviewResult,
  ImageGenerationAccessState,
  ImageGenerationDraftSession,
} from "@/features/image-generation";
import { subscriptionStore } from "@/stores/subscriptionStore";

export interface ProjectImageStoreDependencies {
  confirmImagePreview: typeof confirmImagePreviewClient;
  requestImagePreviewForDraft: typeof requestImagePreviewForDraft;
}

export interface ProjectImageStoreState {
  approvePreview: () => Promise<ConfirmImagePreviewResult | null>;
  cancelDraftSession: () => void;
  draftSession: ImageGenerationDraftSession | null;
  generatePreview: () => Promise<ImageGenerationDraftSession | null>;
  rejectPreview: () => Promise<ConfirmImagePreviewResult | null>;
  reset: () => void;
  startDraftSession: (session: ImageGenerationDraftSession) => void;
  updateDraftPrompt: (prompt: string) => void;
}

const defaultDependencies: ProjectImageStoreDependencies = {
  confirmImagePreview: confirmImagePreviewClient,
  requestImagePreviewForDraft,
};

function isImageAccessState(value: unknown): value is ImageGenerationAccessState {
  if (!value || typeof value !== "object") {
    return false;
  }

  return (
    "code" in value
    && "creditCost" in value
    && "creditsRemaining" in value
    && "message" in value
  );
}

export function createProjectImageStore(
  deps: ProjectImageStoreDependencies = defaultDependencies,
) {
  return createStore<ProjectImageStoreState>((set, get) => ({
    draftSession: null,

    startDraftSession: (session) => {
      subscriptionStore.getState().applyImageAccessState(session.accessState);

      set({
        draftSession: session,
      });
    },

    updateDraftPrompt: (prompt) => {
      set((state) => ({
        draftSession: state.draftSession
          ? {
              ...state.draftSession,
              editedPrompt: prompt,
            }
          : null,
      }));
    },

    generatePreview: async () => {
      const draftSession = get().draftSession;
      if (!draftSession) {
        return null;
      }

      if (draftSession.accessState.code !== "available") {
        const nextSession = {
          ...draftSession,
          errorMessage: draftSession.accessState.message,
          requestStatus: "error" as const,
        };

        set({
          draftSession: nextSession,
        });

        return nextSession;
      }

      set({
        draftSession: {
          ...draftSession,
          errorMessage: null,
          requestStatus: "loading",
        },
      });

      try {
        const nextSession = await deps.requestImagePreviewForDraft(draftSession);
        subscriptionStore.getState().applyImageAccessState(nextSession.accessState);

        set({
          draftSession: nextSession,
        });

        return nextSession;
      } catch (error) {
        const accessState = isImageAccessState(error) ? error : null;
        let nextSession: ImageGenerationDraftSession | null = null;

        set((state) => ({
          draftSession: (() => {
            if (!state.draftSession) {
              return null;
            }

            nextSession = {
              ...state.draftSession,
              accessState: accessState ?? state.draftSession.accessState,
              creditCost: accessState?.creditCost ?? state.draftSession.creditCost,
              creditsRemaining: accessState?.creditsRemaining ?? state.draftSession.creditsRemaining,
              errorMessage:
                accessState?.message
                ?? (error instanceof Error ? error.message : "IMAGE_GENERATION_FAILED"),
              requestStatus: "error",
            };

            return nextSession;
          })(),
        }));

        if (accessState) {
          subscriptionStore.getState().applyImageAccessState(accessState);
        }

        return nextSession;
      }
    },

    approvePreview: async () => {
      const draftSession = get().draftSession;
      if (!draftSession?.tempAssetRef) {
        return null;
      }

      try {
        const result = await deps.confirmImagePreview({
          decision: "approve",
          projectId: draftSession.projectId,
          sourceId: draftSession.sourceId,
          sourceKind: draftSession.sourceKind,
          tempAssetRef: draftSession.tempAssetRef,
        });

        if (typeof result.creditsRemaining === "number") {
          subscriptionStore.getState().setCreditBalance(result.creditsRemaining);
        }

        set({
          draftSession: null,
        });

        return result;
      } catch (error) {
        const accessState = isImageAccessState(error) ? error : null;

        set((state) => ({
          draftSession: state.draftSession
            ? {
                ...state.draftSession,
                accessState: accessState ?? state.draftSession.accessState,
                creditCost: accessState?.creditCost ?? state.draftSession.creditCost,
                creditsRemaining: accessState?.creditsRemaining ?? state.draftSession.creditsRemaining,
                errorMessage:
                  accessState?.message
                  ?? (error instanceof Error ? error.message : "IMAGE_CONFIRM_FAILED"),
                requestStatus: "error",
              }
            : null,
        }));

        if (accessState) {
          subscriptionStore.getState().applyImageAccessState(accessState);
        }

        return null;
      }
    },

    rejectPreview: async () => {
      const draftSession = get().draftSession;
      if (!draftSession?.tempAssetRef) {
        set({
          draftSession: null,
        });
        return null;
      }

      try {
        const result = await deps.confirmImagePreview({
          decision: "reject",
          projectId: draftSession.projectId,
          sourceId: draftSession.sourceId,
          sourceKind: draftSession.sourceKind,
          tempAssetRef: draftSession.tempAssetRef,
        });

        set({
          draftSession: null,
        });

        return result;
      } catch (error) {
        set((state) => ({
          draftSession: state.draftSession
            ? {
                ...state.draftSession,
                errorMessage:
                  error instanceof Error ? error.message : "IMAGE_CONFIRM_FAILED",
                requestStatus: "error",
              }
            : null,
        }));

        return null;
      }
    },

    cancelDraftSession: () => {
      set({
        draftSession: null,
      });
    },

    reset: () => {
      set({
        draftSession: null,
      });
    },
  }));
}

export const projectImageStore = createProjectImageStore();

export function useProjectImageStore<T>(
  selector: (state: ProjectImageStoreState) => T,
) {
  return useStore(projectImageStore, selector);
}
